'use client';

// Every conversation this browser is holding, along the foot of the window.
//
// One row, and a thread occupies exactly one slot in it whether it is a pill or
// a window: expanding does not open a panel somewhere else, it grows the pill
// you pressed into the container. That is the whole idea, and it is why the
// windows live in this row rather than in a layer above it — two rows would be
// a switcher and a viewer, which is the arrangement this replaced.
//
// **It is literally one element, on both layouts.** A conversation renders as a
// single `.chatwin`, wearing `.is-folded` while it is a pill, and opening it
// changes nothing but the class. The reason that matters is what it replaced —
// a pill removed from the DOM and a panel inserted somewhere else, dissolved
// between, so however well the rectangle interpolated, what you watched was two
// things swapping. There is no seam to hide here because there is no second
// thing.
//
// The row is fixed to the window rather than being a grid track, because a
// 560px window in a grid row would push the page up every time one was opened.
// The shell keeps an empty track of `--rail` underneath instead, so nothing
// scrolls under the pills; everything taller than that floats.
//
// What differs on a phone is only how the change is drawn. A desktop box grows
// in place, so CSS transitions the width and the height and there is nothing
// else to do. A phone box goes from a pill in the rail's flow to a sheet fixed
// over the screen, and `position` is not a property anything can interpolate —
// so the browser is told instead: same element, one name, two rectangles, and a
// view transition carries it across. See `morph.ts`. The pill leaves the rail
// while it is the sheet, exactly as on a desktop; the rest of the rail stays
// put underneath, which is what keeps the other conversations reachable.

import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Cancel01Icon,
  HistoryIcon,
  MinusSignIcon,
  PlusSignIcon,
} from '@hugeicons/core-free-icons';
import { ago, bySpan } from '@/lib/sessions';
import { useConsole, type Thread } from '../console';
import { Conversation } from './conversation';
import { HISTORY_MORPH, NEW_MORPH, morph, morphName } from './morph';

/** How long the box takes to fold or unfold — `--fold` in globals.css. */
const FOLD_MS = 340;
/** And to collapse out of the row when it is closed — `--shut`. */
const SHUT_MS = 220;

/**
 * The property whose ending means a box has finished leaving.
 *
 * A box on the rail collapses its width so the row closes up behind it. A sheet
 * has no width to collapse — it is pinned to both edges of the screen — so it
 * fades instead. Both run for `--shut` and start together, so either one
 * arriving means the same thing, and `useShutting` drops the second.
 */
const leaving = (property: string) => property === 'width' || property === 'opacity';

/**
 * Fold a box, drawn the way the layout can draw it.
 *
 * A desktop box changes size where it stands, which CSS can transition on its
 * own — starting a view transition over the top of that would photograph a box
 * that is already moving. A phone box changes what kind of positioned thing it
 * is, which CSS cannot transition at all, so the browser is handed the change
 * and told the two rectangles belong to one element.
 */
function useFold() {
  const { wide } = useConsole();
  return (change: () => void) => (wide ? change() : morph(change));
}

/** What to call a thread, wherever it is named. */
function useTitle() {
  const { sessions } = useConsole();
  return (t: Thread) =>
    t.session
      ? (sessions.find(s => s.session_id === t.session?.session_id)?.title ?? 'Conversation')
      : 'New chat';
}

/**
 * Three states and one element, built out of `.spark`'s material — see the
 * light rules in globals.css. Working beats unread: a thread you left, that
 * finished, and that you then sent to again is working, and saying both at once
 * would be two lights arguing.
 */
function state(t: Thread) {
  return t.busy ? 'is-working' : t.unread ? 'is-unread' : 'is-idle';
}

/**
 * True while `on`, and for one fold afterwards.
 *
 * What is inside a window has to outlive the press that closed it. Unmounting
 * the conversation the moment the class changes empties the box and *then*
 * shrinks it, which is two events where the eye expects one — a panel going
 * blank, followed by a pill appearing. Holding the contents for the length of
 * the fold means what shrinks is the thing you were reading.
 */
function useLingering(on: boolean, ms = FOLD_MS) {
  const [was, setWas] = useState(on);
  const [held, setHeld] = useState(false);

  // Noticed during the render that changed it, rather than in an effect after
  // it: the fold starts on that same commit, and a hold that arrived one paint
  // later would let the box go blank for exactly the frame it matters.
  if (was !== on) {
    setWas(on);
    setHeld(!on);
  }

  useEffect(() => {
    if (!held) return;
    const timer = setTimeout(() => setHeld(false), ms);
    return () => clearTimeout(timer);
  }, [held, ms]);

  return on || held;
}

/**
 * Closing, held open for the length of it.
 *
 * A thread that is closed is gone from the provider on the same tick, and a
 * box that is gone cannot shrink — which is why closing used to be the one
 * thing on this rail that happened all at once. So the press marks it instead,
 * the CSS collapses it, and the thread is actually dropped when there is
 * nothing left to look at.
 *
 * Keyed rather than a boolean, so two closes in a row cannot have the first
 * one's timer take the mark off the second one's box.
 */
function useShutting(close: (key: string) => void) {
  const [shutting, setShutting] = useState<string | null>(null);
  const done = useRef(new Set<string>());

  const drop = (key: string) => {
    // Two things race to call this and only the first should land — see `shut`.
    if (done.current.has(key)) return;
    done.current.add(key);
    setShutting(k => (k === key ? null : k));
    close(key);
  };

  return {
    shutting,
    /** For the box's own `transitionend`: it has finished leaving. */
    finish: drop,
    shut: (key: string) => {
      // Nothing to wait for, so nothing to wait for it in.
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return close(key);
      setShutting(key);
      // The belt to `finish`'s brace, and the wrong way round from how it
      // looks: this is the fallback, not the mechanism. A timer of `--shut`
      // *was* the mechanism at first and it did not work, because it starts
      // when the press is handled and the class does not arrive until React
      // has re-rendered the dock — which in development, with two live
      // conversations in it, is a third of a second. The box was dropped
      // before it had begun to shrink, every time, and nothing about the CSS
      // was wrong. So the box says when it has gone; this only catches the
      // case where it never says anything at all.
      setTimeout(() => drop(key), SHUT_MS + 800);
    },
  };
}

/**
 * A conversation, as one box that changes size.
 *
 * Folded it is a pill on the rail; open it is a window standing in the pill's
 * own slot, or — on a phone — a sheet over the screen. Nothing here branches on
 * which: the class does, and the CSS reads it. That is the whole of what makes
 * the two states one object.
 *
 * The name it carries is what it calls itself while it is moving. One element
 * holds it in both frames, which is the case a view transition is easiest
 * about, and it is why nothing on the rail may ever hand this name to a second
 * box — see `morph.ts`.
 */
function ThreadPanel({
  thread,
  open,
  shutting,
  onShut,
  onShutEnd,
}: {
  thread: Thread;
  open: boolean;
  shutting: boolean;
  onShut: (key: string) => void;
  onShutEnd: (key: string) => void;
}) {
  const { raiseThread, lowerThread, frontKey } = useConsole();
  const title = useTitle()(thread);
  const body = useLingering(open);
  const fold = useFold();

  return (
    <section
      className={
        `chatwin traced ${state(thread)}` +
        (open ? '' : ' is-folded') +
        (shutting ? ' is-closing' : '') +
        (open && thread.key === frontKey ? ' is-front' : '')
      }
      // What the rail scrolls to when this is the one in front — the pill and
      // the window are the same box, so there is one thing to look for.
      data-thread={thread.key}
      style={{ viewTransitionName: morphName(thread.key) }}
      // Pressing anywhere in a window makes it the one the address names, the
      // way clicking a window anywhere brings it forward. `capture` so it still
      // fires when the press was really meant for a button inside.
      onPointerDownCapture={() => {
        if (open && thread.key !== frontKey) raiseThread(thread.key);
      }}
      onTransitionEnd={e => {
        if (shutting && e.target === e.currentTarget && leaving(e.propertyName)) {
          onShutEnd(thread.key);
        }
      }}
    >
      <header className="chatwin-head">
        {/* The name is the whole gesture, both ways: pressing the pill unfolds
            it, pressing the head of the window folds it back. One press in one
            place, whichever size the box currently is. */}
        <button
          className="chatwin-name"
          onClick={() => fold(() => (open ? lowerThread(thread.key) : raiseThread(thread.key)))}
          aria-expanded={open}
          title={`${title}${thread.busy ? ' — working' : thread.unread ? ' — finished' : ''}`}
        >
          <span className="tab-light" aria-hidden="true" />
          <span className="chatwin-title">{title}</span>
          <span className="sr-only">
            {thread.busy ? ' — working' : thread.unread ? ' — finished, not read' : ''}
          </span>
        </button>
        <button
          className="chatwin-btn is-fold"
          onClick={() => fold(() => lowerThread(thread.key))}
          aria-label="Fold this conversation down"
          title="Fold down"
          tabIndex={open ? undefined : -1}
        >
          <HugeiconsIcon icon={MinusSignIcon} size={15} strokeWidth={2.4} />
        </button>
        <button
          className="chatwin-btn is-shut"
          onClick={() => onShut(thread.key)}
          aria-label={`Close "${title}"`}
          title="Close"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={15} strokeWidth={2.4} />
        </button>
      </header>
      {body && <Conversation thread={thread} />}
    </section>
  );
}

/**
 * The way to start one — and, while you are starting one, the thing you are
 * starting it in.
 *
 * A box of the same kind as a conversation and as the history: folded it is the
 * button, unfolded it is the draft. Pressing it the first time makes a draft and
 * opens it; pressing it again puts it down with whatever is half-typed still in
 * it; pressing it once more brings that back.
 *
 * What it deliberately does not do is make a panel. A conversation gets a panel
 * of its own in the row when its first message goes, which is the moment it
 * becomes a conversation — before that there is nothing for a panel in the row
 * to be about, and there used to be one anyway, standing beside the control
 * that had made it.
 */
function NewPanel({
  draft,
  open,
  shutting,
  onShut,
  onShutEnd,
}: {
  draft: Thread | null;
  open: boolean;
  shutting: boolean;
  onShut: (key: string) => void;
  onShutEnd: (key: string) => void;
}) {
  const { raiseThread, lowerThread, newChat, frontKey, wide } = useConsole();
  const body = useLingering(open);
  const fold = useFold();

  return (
    <section
      className={
        'chatwin is-new traced' +
        (open ? '' : ' is-folded') +
        (shutting ? ' is-closing' : '') +
        (draft ? ' has-draft' : '') +
        (open && draft?.key === frontKey ? ' is-front' : '')
      }
      // Phone only, like the history's. On a desktop the box grows where it
      // stands and the CSS has it; naming it there would also pair this panel
      // with its own folded button on the one press that turns a draft into a
      // conversation, which is a fold nobody asked for on top of a window
      // arriving.
      style={wide ? undefined : { viewTransitionName: NEW_MORPH }}
      onPointerDownCapture={() => {
        if (open && draft && draft.key !== frontKey) raiseThread(draft.key);
      }}
      onTransitionEnd={e => {
        if (shutting && draft && e.target === e.currentTarget && leaving(e.propertyName)) {
          onShutEnd(draft.key);
        }
      }}
    >
      <header className="chatwin-head">
        <button
          className="chatwin-name"
          onClick={() =>
            fold(() => {
              if (!draft) newChat();
              else if (open) lowerThread(draft.key);
              else raiseThread(draft.key);
            })
          }
          aria-expanded={open}
          title="Start a new conversation"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={14} strokeWidth={2.4} />
          <span className="chatwin-title">New</span>
        </button>
        {/* Only once there is something to put down or throw away. An empty New
            offering to fold and close itself is two buttons for nothing. */}
        {draft && (
          <>
            <button
              className="chatwin-btn is-fold"
              onClick={() => fold(() => lowerThread(draft.key))}
              aria-label="Put this draft down"
              title="Fold down"
              tabIndex={open ? undefined : -1}
            >
              <HugeiconsIcon icon={MinusSignIcon} size={15} strokeWidth={2.4} />
            </button>
            <button
              className="chatwin-btn is-shut"
              onClick={() => onShut(draft.key)}
              aria-label="Discard this draft"
              title="Discard"
              tabIndex={open ? undefined : -1}
            >
              <HugeiconsIcon icon={Cancel01Icon} size={15} strokeWidth={2.4} />
            </button>
          </>
        )}
      </header>
      {body && draft && <Conversation thread={draft} />}
    </section>
  );
}

/**
 * Everything the runner has on disk.
 *
 * A different question from what is open, and the one a list is actually good
 * at: you come here to choose one thing, which is exactly what a list is for
 * and exactly what it was doing badly when it was also trying to be the
 * switcher.
 *
 * The same box as a conversation, folded the same way: `folded` is the History
 * pill and `open` is the panel, and there is no second element on either
 * layout.
 */
/* No "New chat" at the foot of it. New is a pill on the rail, two inches from
 * the thumb and on screen in every view — the button in here was the same
 * action, further away, in a panel you had opened to do something else. */
function Archive({ open }: { open: boolean }) {
  const {
    sessions, threads, manyWorkspaces, error, showChat, forget, arming, setArming,
    setArchiveOpen, raiseArchive, wide,
  } = useConsole();
  const body = useLingering(open);
  const fold = useFold();

  return (
    <section
      className={`chatwin is-archive traced${open ? '' : ' is-folded'}`}
      // Phone only: there the box goes from the rail's flow to a sheet fixed
      // over the screen, and this is what the browser follows across. On a
      // desktop it grows where it stands and the CSS has it.
      style={wide ? undefined : { viewTransitionName: HISTORY_MORPH }}
    >
      <header className="chatwin-head">
        <button
          className="chatwin-name"
          onClick={() => fold(() => (open ? setArchiveOpen(false) : raiseArchive()))}
          aria-expanded={open}
          title="Every conversation the runner has"
        >
          <HugeiconsIcon icon={HistoryIcon} size={15} strokeWidth={2.2} />
          <span className="chatwin-title">History</span>
        </button>
        <button
          className="chatwin-btn is-shut"
          onClick={() => fold(() => setArchiveOpen(false))}
          aria-label="Close the history"
          title="Close"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={15} strokeWidth={2.4} />
        </button>
      </header>

      {body && (
        <>
          <div className="chatbar-body">
            <nav className="chat-list">
              {/* Cut into spans, which is what a flat run of ages could not
                  show: the edge between this morning and last week is the one
                  people navigate by, and it was costing a reading of every row
                  to find. Headings buy that for a line each. */}
              {bySpan(
                // Minus whatever is already open. One conversation on two rows,
                // one of them live and one of them not, is two things as far as
                // anyone reading the list is concerned.
                sessions.filter(s => !threads.some(t => t.session?.session_id === s.session_id)),
                s => s.last_active,
              ).map(([span, inSpan]) => (
                <div className="ws-group" key={span}>
                  <div className="section-label">{span}</div>
                  {inSpan.map(s => (
                    <div className="chat-row" key={s.session_id}>
                      <button
                        className="chat"
                        onClick={() => {
                          setArchiveOpen(false);
                          showChat(s.session_id);
                        }}
                      >
                        <span className="chat-title">{s.title}</span>
                        {/* The workspace only when there is more than one to
                            tell apart. With every conversation in the same repo
                            it was the same word down the whole column, in mono,
                            at the same weight as the age beside it — drawing
                            the eye every row to say nothing. */}
                        <span className="chat-meta">
                          {manyWorkspaces
                            ? `${s.workspace} · ${ago(s.last_active)}`
                            : ago(s.last_active)}
                        </span>
                      </button>
                      {arming === s.session_id ? (
                        <button
                          className="chat-forget is-armed"
                          onClick={() => void forget(s.session_id)}
                          onBlur={() => setArming(null)}
                          autoFocus
                        >
                          Forget?
                        </button>
                      ) : (
                        <button
                          className="chat-forget"
                          onClick={() => setArming(s.session_id)}
                          aria-label={`Forget "${s.title}"`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {!sessions.length && !threads.length && !error && (
                <div className="chat-empty">
                  <p>No conversations yet.</p>
                  <p>Start one about whatever is open beside this.</p>
                </div>
              )}
            </nav>
          </div>
        </>
      )}
    </section>
  );
}

export function ChatDock() {
  const {
    threads, openKeys, archiveOpen, closeThread, frontKey,
  } = useConsole();
  const rail = useRef<HTMLDivElement>(null);
  const { shutting, shut, finish } = useShutting(closeThread);

  /**
   * The draft the New control is holding: a conversation with no session yet,
   * which is to say one nobody has sent to.
   *
   * It has no pill and no panel of its own, because New *is* its pill — press
   * New and there it is. The newest one, and only the newest: `beginDraft`
   * reuses an untouched draft rather than making a second, so there is normally
   * one at most, and any older one left over from a path that made two stays in
   * the row where it can still be reached rather than vanishing behind this.
   */
  const drafts = threads.filter(t => !t.session);
  const held = drafts.length ? drafts[drafts.length - 1] : null;
  const inRow = threads.filter(t => t !== held);
  const heldOpen = !!held && openKeys.includes(held.key);

  // Keep the one in front in view. On a phone the row scrolls rather than
  // wrapping — a second row would push the composer down every time a
  // conversation was opened — so with enough threads the active pill can be off
  // the end of it, and coming back to a conversation you cannot see the pill of
  // is the one thing that would make this worse than the list it replaced.
  //
  // By key rather than by state, because the box in front is a pill on the rail
  // or a sheet over the screen depending on nothing this needs to know: a sheet
  // is out of the row's flow, so scrolling to it is a no-op, and the scroll that
  // matters is the one on the way back down.
  useEffect(() => {
    if (!frontKey) return;
    rail.current
      ?.querySelector(`[data-thread="${CSS.escape(frontKey)}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [frontKey, threads.length]);

  // Never nothing, even with no conversations open. This is the only way to
  // reach the history or start a chat now that the pane is a window rather than
  // a column — returning null on an empty list left a fresh desktop with no way
  // in at all, which a screenshot found and no amount of reading the JSX would.
  return (
    <div className="tabs" aria-label="Conversations">
      {/* `display: contents` on a desktop, so what is below is one flat row with
          one gap: a conversation has to sit in the same row as the controls to
          be able to grow into the space beside them. On a phone it is a real
          scrolling box again, which is where scrolling is actually needed — and
          where the mask that fades its ends has to come off while a box in it is
          a sheet, because a mask applies to the whole subtree including a child
          that has taken itself out of the flow. It masked the sheet to nothing
          and made it unclickable while leaving it in the DOM, visible and the
          right size, which is a fault no reading of the CSS finds. */}
      {/* The draft is not in here on either layout — New is its place on the
          rail, and a pill beside it would be the same conversation twice. */}
      <div className="tabs-rail" ref={rail}>
        {inRow.map(t => (
          <ThreadPanel
            key={t.key}
            thread={t}
            open={openKeys.includes(t.key)}
            shutting={shutting === t.key}
            onShut={shut}
            onShutEnd={finish}
          />
        ))}
      </div>

      {/* Outside the scrolling row, so they are in the same place whatever is in
          it. A control that slides away as conversations accumulate is a control
          you have to find.
          *
          Outside it, and otherwise identical to what is in it: same pill, same
          padding, same gap, same 8px between one and the next. They open panels
          in the same place by the same gesture as the conversations do, so a
          second shape for them was a difference that stood for nothing. */}
      <div className="tab-tools">
        {/* This *is* the panel, folded — the same box the conversations use, so
            pressing it grows it rather than swapping it for something else. */}
        <Archive open={archiveOpen} />

        {/* A word rather than a glyph, and always there: starting a conversation
            is the one thing this rail is for that has nothing on it yet, so it
            is the one control that cannot ask to be recognised.
            *
            And this *is* the draft, folded — one box, two sizes, the same as
            everything else on the row. */}
        <NewPanel
          draft={held}
          open={heldOpen}
          shutting={!!held && shutting === held.key}
          onShut={shut}
          onShutEnd={finish}
        />
      </div>
    </div>
  );
}
